import {
  ensureRichTextHtml,
  richTextToPlainText,
} from "../../../utils/riskReviewTemplate";
import {
  bandToColor,
  getCellScore,
  getReadableTextColor,
  normalizeMatrixConfig,
  scoreToBand,
} from "../../../utils/riskMatrixLogic";
import { normalizeControlEffectivenessKey } from "../../../utils/controlEffectiveness";
import { PRINT_FIELD_DEFINITIONS, PRINT_FIELD_MAP } from "./riskPrintConfig";

const LONG_TEXT_LIMITS = {
  description: 520,
  latest_note: 340,
};

const LIST_LIMITS = {
  linked_controls: 4,
  linked_actions: 4,
  tags: 8,
};

const normalizeText = (value) => String(value || "").replace(/\s+/g, " ").trim();

const toPlainText = (value) => normalizeText(richTextToPlainText(ensureRichTextHtml(value || "")));

const toDisplayDate = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";
  return parsed.toLocaleDateString();
};

const toDisplayDateTime = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";
  return parsed.toLocaleString();
};

const truncateText = (value, maxLength) => {
  const text = normalizeText(value);
  if (!text) {
    return { value: "-", truncated: false };
  }
  if (!maxLength || text.length <= maxLength) {
    return { value: text, truncated: false };
  }
  return {
    value: `${text.slice(0, Math.max(0, maxLength - 3)).trimEnd()}...`,
    truncated: true,
  };
};

const truncateList = (items, maxItems) => {
  const normalizedItems = (items || []).map((item) => normalizeText(item)).filter(Boolean);
  if (normalizedItems.length === 0) {
    return { items: [], truncated: false, hiddenCount: 0 };
  }
  if (!maxItems || normalizedItems.length <= maxItems) {
    return { items: normalizedItems, truncated: false, hiddenCount: 0 };
  }
  return {
    items: normalizedItems.slice(0, maxItems),
    truncated: true,
    hiddenCount: normalizedItems.length - maxItems,
  };
};

const parseAssessmentScore = (value) => {
  const match = String(value || "").match(/-?\d+(?:\.\d+)?/);
  return match ? Number(match[0]) : null;
};

const parseDate = (value) => {
  const parsed = new Date(value || "");
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const normalizeActionStatus = (value) =>
  normalizeText(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ");

const resolveTrendLabel = (risk, auditLog) => {
  const latestAssessment = [...(Array.isArray(auditLog) ? auditLog : [])]
    .filter((entry) => String(entry?.field || "").trim().toLowerCase() === "assessment")
    .sort((left, right) => {
      const leftTime = new Date(left?.changed_at || 0).getTime();
      const rightTime = new Date(right?.changed_at || 0).getTime();
      return rightTime - leftTime;
    })[0];

  const previousScore = parseAssessmentScore(latestAssessment?.from_value);
  const currentScore = parseAssessmentScore(latestAssessment?.to_value);

  if (previousScore != null && currentScore != null) {
    if (currentScore > previousScore) return "Worsening";
    if (currentScore < previousScore) return "Improving";
    return "Stable";
  }

  if (risk?.inherent_score != null && risk?.residual_score != null) {
    if (Number(risk.residual_score) < Number(risk.inherent_score)) return "Reduced from inherent";
    if (Number(risk.residual_score) > Number(risk.inherent_score)) return "Higher than inherent";
    return "Stable";
  }

  return "-";
};

const resolveLatestNote = (risk, auditLog) => {
  const latestAuditNote = [...(Array.isArray(auditLog) ? auditLog : [])]
    .filter((entry) => normalizeText(entry?.note))
    .sort((left, right) => {
      const leftTime = new Date(left?.changed_at || 0).getTime();
      const rightTime = new Date(right?.changed_at || 0).getTime();
      return rightTime - leftTime;
    })[0];

  if (latestAuditNote) {
    return {
      text: toPlainText(latestAuditNote.note),
      meta: [
        normalizeText(latestAuditNote.changed_by),
        toDisplayDateTime(latestAuditNote.changed_at),
      ]
        .filter((part) => part && part !== "-")
        .join(" | "),
    };
  }

  return {
    text: toPlainText(risk?.review_notes || ""),
    meta: risk?.last_reviewed_at ? toDisplayDateTime(risk.last_reviewed_at) : "",
  };
};

const resolveTags = (risk) => {
  if (Array.isArray(risk?.tags)) {
    return risk.tags.map((tag) => normalizeText(tag?.name || tag)).filter(Boolean);
  }
  if (Array.isArray(risk?.tag_names)) {
    return risk.tag_names.map((tag) => normalizeText(tag)).filter(Boolean);
  }
  return [];
};

const summarizeControlEffectiveness = (linkedControls) => {
  const counts = {
    effective: 0,
    "partially effective": 0,
    ineffective: 0,
    unrated: 0,
  };

  (linkedControls || []).forEach((control) => {
    const key = normalizeControlEffectivenessKey(control?.effectiveness_rating, { blankKey: "unrated" });
    if (key === "effective") counts.effective += 1;
    else if (key === "needs_improvement") counts["partially effective"] += 1;
    else if (key === "ineffective") counts.ineffective += 1;
    else counts.unrated += 1;
  });

  const parts = [];
  if (counts.effective) parts.push(`${counts.effective} effective`);
  if (counts["partially effective"]) parts.push(`${counts["partially effective"]} partially effective`);
  if (counts.ineffective) parts.push(`${counts.ineffective} ineffective`);
  if (!parts.length && counts.unrated) parts.push(`${counts.unrated} unrated`);
  return parts.join(", ") || "-";
};

const buildControlsSummaryItems = (linkedControls) => {
  const now = Date.now();
  const counts = {
    overdue: 0,
    effective: 0,
    partially_effective: 0,
    ineffective: 0,
  };

  (linkedControls || []).forEach((control) => {
    const nextTestDue = parseDate(control?.next_test_due);
    if (nextTestDue && nextTestDue.getTime() < now) {
      counts.overdue += 1;
    }

    const effectiveness = normalizeControlEffectivenessKey(control?.effectiveness_rating);
    if (effectiveness === "effective") counts.effective += 1;
    else if (effectiveness === "needs_improvement") counts.partially_effective += 1;
    else if (effectiveness === "ineffective") counts.ineffective += 1;
  });

  return [
    { key: "overdue", label: "Overdue", count: counts.overdue },
    { key: "effective", label: "Effective", count: counts.effective },
    { key: "partially_effective", label: "Partially effective", count: counts.partially_effective },
    { key: "ineffective", label: "Ineffective", count: counts.ineffective },
  ].filter((item) => item.count > 0);
};

const buildActionsSummaryItems = (riskActions) => {
  const now = Date.now();
  const counts = {
    overdue: 0,
    in_progress: 0,
    open: 0,
    completed: 0,
  };

  (riskActions || []).forEach((action) => {
    const status = normalizeActionStatus(action?.status);
    const dueDate = parseDate(action?.due_date);
    const isCompleted = status === "completed";
    if (dueDate && !isCompleted && dueDate.getTime() < now) {
      counts.overdue += 1;
    }

    if (status === "in progress") counts.in_progress += 1;
    else if (status === "completed") counts.completed += 1;
    else counts.open += 1;
  });

  return [
    { key: "overdue", label: "Overdue", count: counts.overdue },
    { key: "in_progress", label: "In progress", count: counts.in_progress },
    { key: "open", label: "Open", count: counts.open },
    { key: "completed", label: "Completed", count: counts.completed },
  ].filter((item) => item.count > 0);
};

const buildScoreField = (key, label, scoreValue, matrixConfig) => {
  const score = Number(scoreValue);
  const hasScore = Number.isFinite(score);
  const band = hasScore ? scoreToBand({ score, bandThresholds: matrixConfig?.band_thresholds }) : null;
  const bandColor = band ? bandToColor({ band, bandColors: matrixConfig?.band_colors }) : null;
  const textColor = bandColor ? getReadableTextColor(bandColor) : "#111827";

  return {
    label,
    type: "score",
    key,
    value: hasScore ? String(scoreValue) : "-",
    band: band || "-",
    bandColor,
    textColor,
  };
};

const buildMatrixField = (risk, matrixConfig) => {
  const resolvedMatrix = normalizeMatrixConfig(matrixConfig);
  const matrixSize = Number(resolvedMatrix?.matrix_size) || 5;
  const inherentPosition =
    Number.isInteger(Number(risk?.likelihood)) && Number.isInteger(Number(risk?.impact))
      ? { likelihood: Number(risk.likelihood), impact: Number(risk.impact), marker: "I" }
      : null;

  const residualLikelihood = Number.isInteger(Number(risk?.residual_likelihood))
    ? Number(risk.residual_likelihood)
    : Number.isInteger(Number(risk?.likelihood))
      ? Number(risk.likelihood)
      : null;
  const residualImpact = Number.isInteger(Number(risk?.residual_impact))
    ? Number(risk.residual_impact)
    : Number.isInteger(Number(risk?.impact))
      ? Number(risk.impact)
      : null;
  const residualPosition =
    residualLikelihood != null && residualImpact != null
      ? { likelihood: residualLikelihood, impact: residualImpact, marker: "R" }
      : null;

  const cells = [];
  for (let likelihood = matrixSize; likelihood >= 1; likelihood -= 1) {
    for (let impact = 1; impact <= matrixSize; impact += 1) {
      const score = getCellScore({
        scoringMode: resolvedMatrix?.scoring_mode,
        cellScores: resolvedMatrix?.cell_scores,
        lik: likelihood,
        imp: impact,
      });
      const band = scoreToBand({ score, bandThresholds: resolvedMatrix?.band_thresholds });
      const color = bandToColor({ band, bandColors: resolvedMatrix?.band_colors });
      const textColor = getReadableTextColor(color);
      const markers = [];
      if (inherentPosition?.likelihood === likelihood && inherentPosition?.impact === impact) markers.push("I");
      if (residualPosition?.likelihood === likelihood && residualPosition?.impact === impact) markers.push("R");
      cells.push({
        key: `${likelihood}-${impact}`,
        likelihood,
        impact,
        score,
        band,
        color,
        textColor,
        markers,
      });
    }
  }

  return {
    label: "Risk matrix",
    type: "matrix",
    matrixSize,
    cells,
    legend: [
      inherentPosition ? { label: "I", text: "Inherent" } : null,
      residualPosition ? { label: "R", text: "Residual" } : null,
    ].filter(Boolean),
  };
};

export function buildRiskPrintFieldMap({
  risk,
  ownerLabel,
  auditLog,
  linkedControls,
  riskActions,
  matrixConfig,
}) {
  const resolvedMatrix = normalizeMatrixConfig(matrixConfig);
  const latestNote = resolveLatestNote(risk, auditLog);
  const tagItems = resolveTags(risk);
  const linkedControlItems = (linkedControls || []).map((control) =>
    [control?.name, normalizeText(control?.status), normalizeText(control?.effectiveness_rating)]
      .filter(Boolean)
      .join(" | ")
  );
  const linkedActionItems = (riskActions || []).map((action) =>
    [
      normalizeText(action?.title || action?.name),
      normalizeText(action?.status),
      normalizeText(action?.priority),
    ]
      .filter(Boolean)
      .join(" | ")
  );

  return {
    risk_name: { label: "Risk name", type: "text", value: normalizeText(risk?.name) || "-" },
    risk_id: { label: "Risk ID", type: "text", value: risk?.id != null ? `#${risk.id}` : "-" },
    description: { label: "Description", type: "longText", value: toPlainText(risk?.description || "") },
    owner: { label: "Owner", type: "text", value: normalizeText(ownerLabel) || "Unassigned" },
    status: { label: "Status", type: "text", value: normalizeText(risk?.status) || "-" },
    category: { label: "Category", type: "text", value: normalizeText(risk?.category) || "-" },
    inherent_score: buildScoreField("inherent_score", "Inherent score", risk?.inherent_score, resolvedMatrix),
    residual_score: buildScoreField("residual_score", "Residual score", risk?.residual_score, resolvedMatrix),
    risk_matrix: buildMatrixField(risk, resolvedMatrix),
    control_effectiveness: {
      label: "Control effectiveness",
      type: "text",
      value: summarizeControlEffectiveness(linkedControls),
    },
    trend: { label: "Trend", type: "text", value: resolveTrendLabel(risk, auditLog) },
    latest_note: {
      label: "Latest note",
      type: "longText",
      value: latestNote.text || "-",
      meta: latestNote.meta,
    },
    last_updated: { label: "Last updated", type: "text", value: toDisplayDateTime(risk?.updated_at) },
    review_date: {
      label: "Review date",
      type: "text",
      value: toDisplayDate(risk?.next_review_date || risk?.last_reviewed_at),
    },
    linked_controls: {
      label: "Linked controls",
      type: "list",
      items: linkedControlItems,
      emptyLabel: "No linked controls",
      summaryItems: buildControlsSummaryItems(linkedControls),
    },
    linked_actions: {
      label: "Linked actions / treatment plans",
      type: "list",
      items: linkedActionItems,
      emptyLabel: "No linked actions or treatment plans",
      summaryItems: buildActionsSummaryItems(riskActions),
    },
    tags: {
      label: "Tags",
      type: "tag",
      items: tagItems,
      emptyLabel: "No tags",
    },
  };
}

export function buildRiskPrintPreview(selectedKeys, fieldMap, subOptions = {}) {
  const orderedKeys = PRINT_FIELD_DEFINITIONS.map((field) => field.key).filter((key) => selectedKeys.includes(key));
  const sections = {
    header: [],
    summary: [],
    matrix: [],
    detail: [],
    badges: [],
    list: [],
    tag: [],
  };
  const truncation = {};

  orderedKeys.forEach((key) => {
    const definition = PRINT_FIELD_MAP[key];
    const source = fieldMap[key];
    if (!definition || !source) return;

    if (source.type === "longText") {
      const truncated = truncateText(source.value, LONG_TEXT_LIMITS[key]);
      sections[definition.section].push({
        ...source,
        key,
        value: truncated.value,
        truncated: truncated.truncated,
      });
      truncation[key] = truncated.truncated;
      return;
    }

    if (source.type === "list" || source.type === "tag") {
      const limited = truncateList(source.items, LIST_LIMITS[key]);
      const summaryOptionKey = key === "linked_controls" ? "linked_controls_summary" : key === "linked_actions" ? "linked_actions_summary" : null;
      if (summaryOptionKey && subOptions?.[summaryOptionKey] && Array.isArray(source.summaryItems) && source.summaryItems.length > 0) {
        sections.badges.push({
          key: `${key}_summary`,
          label: `${source.label} summary`,
          type: "badgeGroup",
          items: source.summaryItems,
        });
      }
      sections[definition.section].push({
        ...source,
        key,
        items: limited.items,
        truncated: limited.truncated,
        hiddenCount: limited.hiddenCount,
      });
      truncation[key] = limited.truncated;
      return;
    }

    if (source.type === "matrix") {
      sections.matrix.push({ ...source, key, truncated: false });
      truncation[key] = false;
      return;
    }

    sections[definition.section].push({
      ...source,
      key,
      value: normalizeText(source.value) || "-",
      truncated: false,
    });
    truncation[key] = false;
  });

  return { sections, orderedKeys, truncation };
}

export function estimateRiskPrintOverflow(selectedKeys, fieldMap, subOptions = {}) {
  const preview = buildRiskPrintPreview(selectedKeys, fieldMap, subOptions);
  const score = preview.orderedKeys.reduce((total, key) => {
    const field = fieldMap[key];
    if (!field) return total;
    if (field.type === "longText") {
      return total + 10 + Math.ceil((normalizeText(field.value).length || 0) / 180);
    }
    if (field.type === "list" || field.type === "tag") {
      const items = Array.isArray(field.items) ? field.items : [];
      return total + 5 + items.length * 2;
    }
    if (field.type === "matrix") {
      return total + 14;
    }
    return total + 3 + Math.ceil((normalizeText(field.value).length || 0) / 48);
  }, 0);

  const badgeScore = preview.sections.badges.reduce((total, group) => total + 2 + (group.items?.length || 0), 0);
  const overflowScore = score + badgeScore;

  return {
    score: overflowScore,
    likelyOverflow: overflowScore > 34,
  };
}
